import { ExamStaticsJobInfo, ExamStatsTab } from '../../../../../store/workspace/types';
import { camelCaseToSpacedString } from '../../../../../utils/generalHelpers';
import DropDown from '../../../../../components/DropDown';
import { ExamStatsTabItem } from '../../constants';
import styles from './style.module.scss';

export interface ExamTabDescriptor extends ExamStatsTabItem {
    /** Tabs that are neither visible nor reachable are simply omitted. */
    visible: boolean;
    /** A pending exam is displayed greyed out and cannot be selected. */
    disabled?: boolean;
};

/**
 * Resolves which exam tabs the job actually offers. A pending exam is still
 * advertised, but greyed out until its questions have been generated.
 */
export function buildExamTabs(
    jobInfo: ExamStaticsJobInfo | null,
    items: ExamStatsTabItem[],
): ExamTabDescriptor[] {
    return items.map(item => {
        switch (item.id) {
            case 'recruitingInterview':
                return { ...item, visible: jobInfo?.recruitingInterview === 'enable' };
            case 'multipleChoice':
                return {
                    ...item,
                    visible: jobInfo?.multipleChoiceExam === 'enable' || jobInfo?.multipleChoiceExam === 'pending',
                    disabled: jobInfo?.multipleChoiceExam === 'pending',
                };
            case 'coding':
                return {
                    ...item,
                    visible: jobInfo?.codingExam === 'enable' || jobInfo?.codingExam === 'pending',
                    disabled: jobInfo?.codingExam === 'pending',
                };
            default:
                return { ...item, visible: true };
        }
    });
};

interface Props {
    activeTab: ExamStatsTab;
    tabs: ExamTabDescriptor[];
    onChange: (tab: ExamStatsTab) => void;
};

/**
 * Tab strip on desktop, dropdown below 1216px — shared by the applicants list,
 * the exam sheet and the exam results screens. Both layouts are driven by the
 * same descriptors, so the mobile list always mirrors what the job offers.
 */
function ExamTabs(props: Props) {
    const { tabs, activeTab, onChange } = props;

    const visibleTabs = tabs.filter(tab => tab.visible);
    const selectedTab = visibleTabs.find(tab => tab.id === activeTab);

    const selectTab = (tab: ExamTabDescriptor) => {
        if (tab.disabled) return;
        onChange(tab.id);
    };

    return <>
        <div className={styles.tabs}>
            {visibleTabs.map(tab => <div
                key={tab.id}
                className={styles.tabs_item}
                data-is-active={activeTab === tab.id}
                data-is-disable={tab.disabled === true}
                onClick={() => selectTab(tab)}
            >
                <span>{tab.title}</span>
            </div>)}
        </div>

        <DropDown
            idKey='id'
            titleKey='title'
            dropDownList={visibleTabs}
            containerClassName={styles.dropdown}
            setItem={(item: ExamTabDescriptor) => selectTab(item)}
            selectedItem={{
                id: activeTab,
                title: selectedTab?.title ?? camelCaseToSpacedString(activeTab || ''),
            }}
        />
    </>;
};

export default ExamTabs;
